/**
 * accessibility-modal.js
 *
 * Opens/closes the accessibility settings modal, persists user preferences
 * to localStorage, and applies changes immediately to the document.
 *
 * Relies on globals from global.js (loaded before this file via defer order):
 *   focusTrap(element), releaseFocusTrap(), announceToScreenReader(message)
 *
 * Storage key: 'hattie-a11y' (shape defined in global.js)
 * Cookie notice: 'hattie-cookies' → 'accepted' (dismisses bar only; does not gate scripts)
 * Trigger seen: 'hattie-a11y-trigger-seen' → 'true' (minimises trigger to icon-only)
 */

const A11Y_TRIGGER_SEEN_KEY = 'hattie-a11y-trigger-seen';

class AccessibilityModal {
  constructor() {
    this.modal = document.getElementById('a11y-modal');
    this.triggers = document.querySelectorAll('[data-a11y-trigger]');
    this.fixedTrigger = document.getElementById('a11y-trigger');
    this.activeTrigger = null;
    this.cookieBar = document.getElementById('cookie-consent');
    this.cookieAcceptBtn = document.getElementById('cookie-accept');
    this.motionToggle = document.getElementById('reduce-motion-toggle');
    this.motionStatus = document.getElementById('reduce-motion-status');
    this.closeBtn = document.getElementById('a11y-modal-close');
    this.isOpen = false;
    this.isClosing = false;
    /** Button that opened the modal from outside the fixed trigger (menu/footer). */
    this.externalOpener = null;

    this._bindCookieEvents();
    this._checkCookieConsent();

    if (!this.modal) return;

    this._ensureClosed();
    this._bindEvents();
    this._syncFromStorage();

    if (this.fixedTrigger) {
      this._bindTriggerView();
    }
  }

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  _isOpen() {
    return this.modal && !this.modal.hasAttribute('hidden');
  }

  _allowsMotion() {
    return typeof prefersReducedMotion !== 'function' || !prefersReducedMotion();
  }

  /** Force closed on init — [hidden] alone is not enough if CSS sets display on .a11y-modal */
  _ensureClosed() {
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('hidden', '');
    if (typeof hideModalBackdrop === 'function') {
      hideModalBackdrop();
    }
    this._setTriggersExpanded(false);
    this.isOpen = false;
    this.isClosing = false;
  }

  _setTriggersExpanded(expanded) {
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  open(opener) {
    if (!this.modal || this._isOpen() || this.isClosing) return;

    if (opener) {
      this.externalOpener = opener;
    }

    this.modal.removeAttribute('hidden');
    this._setTriggersExpanded(true);
    this.isOpen = true;

    if (this._allowsMotion()) {
      this.modal.classList.remove('is-open');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal.classList.add('is-open');
        });
      });
    } else {
      this.modal.classList.add('is-open');
    }

    if (typeof openModalBackdrop === 'function') {
      openModalBackdrop();
    }

    if (typeof focusTrap === 'function') {
      focusTrap(this.modal);
    }

    if (this.closeBtn) {
      this.closeBtn.focus();
    }

    if (typeof announceToScreenReader === 'function') {
      announceToScreenReader('Accessibility settings opened.');
    }
  }

  close() {
    if (!this.modal || !this._isOpen() || this.isClosing) return;

    if (this._allowsMotion()) {
      this.isClosing = true;
      this.modal.classList.remove('is-open');

      const overlay =
        typeof getModalOverlay === 'function' ? getModalOverlay() : null;
      if (overlay) {
        overlay.classList.remove('is-open');
      }

      let finished = false;
      let pending = overlay ? 2 : 1;

      const complete = () => {
        if (finished) return;
        finished = true;
        if (typeof hideModalBackdrop === 'function') {
          hideModalBackdrop();
        }
        this._finishClose();
      };

      const tryComplete = () => {
        pending -= 1;
        if (pending <= 0) complete();
      };

      const onTransitionEnd = (event) => {
        if (event.target === this.modal && event.propertyName === 'transform') {
          tryComplete();
        }
        if (overlay && event.target === overlay && event.propertyName === 'opacity') {
          tryComplete();
        }
      };

      this.modal.addEventListener('transitionend', onTransitionEnd);
      if (overlay) overlay.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(() => {
        this.modal.removeEventListener('transitionend', onTransitionEnd);
        if (overlay) overlay.removeEventListener('transitionend', onTransitionEnd);
        complete();
      }, MODAL_TRANSITION_FALLBACK_MS);
      return;
    }

    if (typeof hideModalBackdrop === 'function') {
      hideModalBackdrop();
    }
    this._finishClose();
  }

  _finishClose() {
    if (!this.modal || this.modal.hasAttribute('hidden')) return;

    if (typeof hideModalBackdrop === 'function') {
      hideModalBackdrop();
    }

    this.modal.setAttribute('hidden', '');
    this.modal.classList.remove('is-open');
    this._setTriggersExpanded(false);
    this.isOpen = false;
    this.isClosing = false;

    if (typeof releaseFocusTrap === 'function') {
      releaseFocusTrap();
    }

    const returnFocusEl = this.externalOpener || this.activeTrigger || this.fixedTrigger;
    this.externalOpener = null;
    this.activeTrigger = null;

    if (returnFocusEl) {
      returnFocusEl.focus();
    }

    if (typeof announceToScreenReader === 'function') {
      announceToScreenReader('Accessibility settings closed.');
    }
  }

  // ---------------------------------------------------------------------------
  // Setting handlers
  // ---------------------------------------------------------------------------

  _handleFontSize(btn) {
    const size = btn.dataset.fontSize;
    document.documentElement.style.fontSize = size + '%';
    this._setPressed(btn, '[data-font-size]');
    this._savePrefs({ fontSize: size });
    if (typeof announceToScreenReader === 'function') {
      announceToScreenReader('Text size changed.');
    }
  }

  _handleGrid(btn) {
    const isBig = btn.dataset.grid === 'big';
    document.documentElement.classList.toggle('grid-big', isBig);
    this._setPressed(btn, '[data-grid]');
    this._savePrefs({ gridBig: isBig });
    announceToScreenReader('Grid size changed.');
  }

  _handleMotion() {
    const isChecked = this.motionToggle.getAttribute('aria-checked') === 'true';
    const next = !isChecked;
    document.documentElement.classList.toggle('reduce-motion', next);
    this.motionToggle.setAttribute('aria-checked', String(next));
    this._setMotionStatus(next);
    this._savePrefs({ reduceMotion: next });
    announceToScreenReader(next ? 'Reduce motion enabled.' : 'Reduce motion disabled.');
  }

  _handleTheme(btn) {
    const n = btn.dataset.colour;
    const value = `var(--primary-colour-${n})`;
    document.documentElement.style.setProperty('--primary-colour', value);
    this._setPressed(btn, '[data-colour]');
    this._savePrefs({ primaryColour: value });
    announceToScreenReader('Theme colour changed.');
  }

  _handleCookieAccept() {
    try {
      localStorage.setItem('hattie-cookies', 'accepted');
    } catch (_) {}
    if (this.cookieBar) this.cookieBar.hidden = true;
    if (typeof announceToScreenReader === 'function') {
      announceToScreenReader('Cookie notice dismissed.');
    }
  }

  // ---------------------------------------------------------------------------
  // State sync — sets initial aria-pressed/aria-checked from localStorage
  // ---------------------------------------------------------------------------

  _syncFromStorage() {
    let prefs = {};
    try {
      prefs = JSON.parse(localStorage.getItem('hattie-a11y') || '{}');
    } catch (_) {}

    // Font size — default to '100' if not set
    const fontSize = prefs.fontSize || '100';
    document.documentElement.style.fontSize = fontSize + '%';
    const fontBtn = this.modal.querySelector(`[data-font-size="${fontSize}"]`);
    if (fontBtn) this._setPressed(fontBtn, '[data-font-size]');

    // Grid size — default to 'default' if not set
    const gridBig = prefs.gridBig === true;
    const gridBtn = this.modal.querySelector(`[data-grid="${gridBig ? 'big' : 'default'}"]`);
    if (gridBtn) this._setPressed(gridBtn, '[data-grid]');

    // Reduce motion switch — defaults to off when no preference is stored
    if (this.motionToggle) {
      const reduceMotion = prefs.reduceMotion === true;
      this.motionToggle.setAttribute('aria-checked', reduceMotion ? 'true' : 'false');
      this._setMotionStatus(reduceMotion);
    }

    // Theme colour — default to colour 1 if not set
    if (prefs.primaryColour) {
      const match = prefs.primaryColour.match(/--primary-colour-(\d)/);
      if (match) {
        const colourBtn = this.modal.querySelector(`[data-colour="${match[1]}"]`);
        if (colourBtn) this._setPressed(colourBtn, '[data-colour]');
      }
    }
  }

  _hasSeenTrigger() {
    try {
      return localStorage.getItem(A11Y_TRIGGER_SEEN_KEY) === 'true';
    } catch (_) {
      return false;
    }
  }

  _markTriggerSeen() {
    try {
      localStorage.setItem(A11Y_TRIGGER_SEEN_KEY, 'true');
    } catch (_) {}
    this._updateTriggerView();
  }

  _isInTopViewport() {
    return window.scrollY < window.innerHeight;
  }

  _updateTriggerView() {
    if (!this.fixedTrigger) return;
    const minimized = this._hasSeenTrigger() || !this._isInTopViewport();
    this.fixedTrigger.classList.toggle('a11y-trigger--minimized', minimized);
  }

  _bindTriggerView() {
    this._updateTriggerView();

    window.addEventListener(
      'scroll',
      () => {
        if (this._hasSeenTrigger()) return;
        this._updateTriggerView();
      },
      { passive: true }
    );
  }

  _checkCookieConsent() {
    let accepted = false;
    try {
      accepted = localStorage.getItem('hattie-cookies') === 'accepted';
    } catch (_) {}
    if (accepted && this.cookieBar) {
      this.cookieBar.hidden = true;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Set aria-pressed="true" on btn, aria-pressed="false" on all siblings
   * matching siblingSelector within the same [role="group"] or parent.
   * @param {HTMLElement} btn
   * @param {string} siblingSelector
   */
  _setMotionStatus(isOn) {
    if (!this.motionStatus) return;
    this.motionStatus.textContent = isOn ? 'On' : 'Off';
  }

  _setPressed(btn, siblingSelector) {
    const group = btn.closest('[role="group"]') || btn.parentElement;
    group.querySelectorAll(siblingSelector).forEach((el) => {
      el.setAttribute('aria-pressed', el === btn ? 'true' : 'false');
    });
  }

  /**
   * Merge patch into the stored 'hattie-a11y' object.
   * @param {object} patch
   */
  _savePrefs(patch) {
    let prefs = {};
    try {
      prefs = JSON.parse(localStorage.getItem('hattie-a11y') || '{}');
    } catch (_) {}
    Object.assign(prefs, patch);
    try {
      localStorage.setItem('hattie-a11y', JSON.stringify(prefs));
    } catch (_) {}
  }

  // ---------------------------------------------------------------------------
  // Event binding
  // ---------------------------------------------------------------------------

  _bindCookieEvents() {
    if (this.cookieAcceptBtn) {
      this.cookieAcceptBtn.addEventListener('click', () => this._handleCookieAccept());
    }
  }

  _bindEvents() {
    // Triggers: toggle modal open/close (fixed desktop + footer mobile)
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.externalOpener = null;
        this.activeTrigger = trigger;
        if (trigger === this.fixedTrigger && !this._hasSeenTrigger()) {
          this._markTriggerSeen();
        }
        if (this._isOpen()) {
          this.close();
        } else {
          this.open();
        }
      });
    });

    // Close button and click outside the panel
    document.addEventListener('click', (event) => {
      if (!this._isOpen()) return;

      if (event.target.closest('#a11y-modal-close')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest('[data-modal-close]')) {
        this.close();
        return;
      }

      if (
        !this.modal.contains(event.target) &&
        !event.target.closest('.accessibility-triggers-wrapper') &&
        !event.target.closest('.footer__utility-row')
      ) {
        this.close();
      }
    });

    // Escape key: close modal
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this._isOpen()) {
        event.preventDefault();
        this.close();
      }
    });

    // Font size buttons
    this.modal.querySelectorAll('[data-font-size]').forEach((btn) => {
      btn.addEventListener('click', () => this._handleFontSize(btn));
    });

    // Grid size buttons
    this.modal.querySelectorAll('[data-grid]').forEach((btn) => {
      btn.addEventListener('click', () => this._handleGrid(btn));
    });

    // Reduce motion switch
    if (this.motionToggle) {
      this.motionToggle.addEventListener('click', () => this._handleMotion());
    }

    // Theme colour buttons
    this.modal.querySelectorAll('[data-colour]').forEach((btn) => {
      btn.addEventListener('click', () => this._handleTheme(btn));
    });

    // Opened programmatically from another component (e.g. menu sidebar).
    document.addEventListener('hattie:a11y-open', (event) => {
      if (this._isOpen()) return;
      const opener = event.detail && event.detail.opener ? event.detail.opener : null;
      this.open(opener);
    });

    // Menu sidebar and footer "Accessibility Settings" buttons.
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-a11y-open]');
      if (!opener) return;

      event.preventDefault();
      this._openFromExternal(opener);
    });
  }

  _openFromExternal(opener) {
    const menuSidebar = document.getElementById('menu-sidebar');
    const isInMenu = menuSidebar && menuSidebar.contains(opener);
    const menuApi = window.HattieMenuSidebar;

    const openModal = () => {
      if (!this._isOpen()) {
        this.open(opener);
      }
    };

    if (isInMenu && menuApi && menuApi.isOpen()) {
      menuApi.close({
        restoreFocus: false,
        onClosed: openModal,
      });
      return;
    }

    openModal();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new AccessibilityModal();
});
